import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Award,
  FileText,
  Download,
  CreditCard,
  XCircle,
  User,
  GraduationCap,
  ArrowRight,
  RefreshCw,
  Calendar,
  Mail,
  Phone
} from 'lucide-react';

const StudentStatus = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [offerLetter, setOfferLetter] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const statusSteps = [
    { id: 'profile', label: 'Profile Completed', icon: User },
    { id: 'pending', label: 'Application Submitted', icon: FileText },
    { id: 'allocated', label: 'Branch Allocated', icon: Award },
    { id: 'accepted', label: 'Offer Accepted', icon: CheckCircle },
    { id: 'paid', label: 'Payment Completed', icon: CreditCard },
    { id: 'completed', label: 'Admission Confirmed', icon: GraduationCap }
  ];

  useEffect(() => {
    fetchStatusData();
  }, []);

  const fetchStatusData = async () => {
    try {
      setRefreshing(true);
      const [profileRes, statusRes] = await Promise.all([
        axios.get('/api/students/profile').catch(() => ({ data: null })),
        axios.get('/api/students/status').catch(() => ({ data: null }))
      ]);
      
      setStudentData(profileRes.data);
      setApplicationStatus(statusRes.data);

      // Try to fetch offer letter if payment is verified
      if (statusRes.data?.paymentStatus === 'verified') {
        try {
          const offerRes = await axios.get('/api/offers/my-offer');
          setOfferLetter(offerRes.data);
        } catch (error) {
          console.log('Offer letter not available yet');
        }
      }
    } catch (error) {
      console.error('Error fetching status:', error);
      toast.error('Failed to fetch application status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleAcceptOffer = async () => {
    try {
      await axios.post('/api/students/accept-offer');
      toast.success('Offer accepted successfully!');
      await fetchStatusData();
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error(error.response?.data?.message || 'Failed to accept offer');
    }
  };

  const handleRejectOffer = async () => {
    if (window.confirm('Are you sure you want to reject this offer? This action cannot be undone.')) {
      try {
        await axios.post('/api/students/reject-offer');
        toast.success('Offer rejected');
        await fetchStatusData();
      } catch (error) {
        console.error('Error rejecting offer:', error);
        toast.error(error.response?.data?.message || 'Failed to reject offer');
      }
    }
  };

  const downloadOfferLetter = async () => {
    try {
          const response = await axios.get('/api/students/offer-letter');
          if (response.data.offerLetterUrl) {
            window.open(response.data.offerLetterUrl, '_blank');
          } else {
            console.error('Offer letter URL not found');
          }
          
        } catch (error) {
          console.error('Error viewing offer letter:', error);
        }
  };

  const previewOfferLetter = async () => {
    try {
          const response = await axios.get('/api/students/offer-letter');
          if (response.data.offerLetterUrl) {
            const inlineUrl = response.data.offerLetterUrl.replace("/upload/fl_attachment/","/upload/")
            window.open(inlineUrl, '_blank');
          } else {
            console.error('Offer letter URL not found');
          }
          
        } catch (error) {
          console.error('Error viewing offer letter:', error);
        } 
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, text: 'Pending' },
      allocated: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Award, text: 'Branch Allocated' },
      accepted: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, text: 'Offer Accepted' },
      completed: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: GraduationCap, text: 'Admission Confirmed' },
      rejected: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, text: 'Application Rejected' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <div className={`inline-flex items-center px-4 py-2 rounded-lg border ${config.color}`}>
        <Icon className="h-5 w-5 mr-2" />
        <span className="font-medium">{config.text}</span>
      </div>
    );
  };

  const getStepStatus = (stepId) => {
    if (!studentData && stepId === 'profile') return 'pending';
    if (!applicationStatus) return 'pending';

    const currentStatus = applicationStatus.status;
    const paymentStatus = applicationStatus.paymentStatus;

    switch (stepId) {
      case 'profile':
        return studentData ? 'completed' : 'pending';
      case 'pending':
        return studentData ? 'completed' : 'pending';
      case 'allocated':
        return ['allocated', 'accepted', 'completed'].includes(currentStatus) ? 'completed' : 'pending';
      case 'accepted':
        return ['accepted', 'completed'].includes(currentStatus) ? 'completed' : 'pending';
      case 'paid':
        return paymentStatus === 'verified' ? 'completed' : 'pending';
      case 'completed':
        return currentStatus === 'completed' ? 'completed' : 'pending';
      default:
        return 'pending';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Application Status</h1>
            <p className="text-gray-600 mt-1">
              Track your admission progress and manage your application
            </p>
          </div>
          <div className="flex flex-col items-start space-y-2 md:flex-row md:items-center md:space-y-0 md:space-x-4">
            <button
              onClick={fetchStatusData}
              disabled={refreshing}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <div>
              {applicationStatus && getStatusBadge(applicationStatus.status)}
            </div>
            
          </div>
        </div>
      </div>

      {/* No Profile Warning */}
      {!studentData && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-400 mr-3" />
            <div>
              <h3 className="text-yellow-800 font-medium">Profile Incomplete</h3>
              <p className="text-yellow-700 text-sm mt-1">
                You need to complete your profile before you can track your application status.
              </p>
            </div>
            <Link
              to="/student/profile"
              className="ml-auto bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors flex items-center"
            >
              Complete Profile
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Application Progress</h2>
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-5 left-6 w-0.5 h-full bg-gray-200 -z-10"></div>
          
          <div className="space-y-6">
            {statusSteps.map((step, index) => {
              const stepStatus = getStepStatus(step.id);
              const Icon = step.icon;
              
              return (
                <div key={step.id} className="relative flex items-center">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                    stepStatus === 'completed' 
                      ? 'bg-green-100 border-green-500 text-green-600' 
                      : 'bg-gray-100 border-gray-300 text-gray-400'
                  }`}>
                    {stepStatus === 'completed' ? (
                      <CheckCircle className="h-6 w-6" />
                    ) : (
                      <Icon className="h-6 w-6" />
                    )}
                  </div>
                  <div className="ml-4 min-w-0 flex-1">
                    <p className={`text-sm font-medium ${
                      stepStatus === 'completed' ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {step.label}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Current Status Details */}
      {studentData && applicationStatus && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Student Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Student Information</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">{studentData.userId.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{studentData.userId.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Phone:</span>
                <span className="font-medium">{studentData.userId.phone}</span>
              </div>
              {applicationStatus.rank && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Rank:</span>
                  <span className="font-medium">#{applicationStatus.rank}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">12th Percentage:</span>
                <span className="font-medium">{studentData.twelfthMarks.percentage.toFixed(2)}%</span>
              </div>
            </div>
          </div>

          {/* Allocation Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Allocation Details</h3>
            {applicationStatus.allocatedBranch ? (
              <div className="space-y-4">
                <div className="bg-primary-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Award className="h-5 w-5 text-primary-600 mr-2" />
                    <span className="font-medium text-primary-900">
                      {applicationStatus.allocatedBranch}
                    </span>
                  </div>
                </div>
                
                {applicationStatus.status === 'allocated' && (
                  <div className="flex space-x-3">
                    <button
                      onClick={handleAcceptOffer}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Accept Offer
                    </button>
                    <button
                      onClick={handleRejectOffer}
                      className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject Offer
                    </button>
                  </div>
                )}

                {applicationStatus.status === 'accepted' && (
                  <div className="text-center">
                    <Link
                      to="/student/payment"
                      className="inline-flex items-center bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <CreditCard className="h-5 w-5 mr-2" />
                      Proceed to Payment
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500">
                <Clock className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p>Branch allocation pending</p>
                <p className="text-xs mt-1">You will be notified once a branch is allocated</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Offer Letter Section */}
      {applicationStatus?.offerLetter?.generated && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FileText className="h-6 w-6 text-primary-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Offer Letter</h3>
                <p className="text-gray-600 text-sm">
                  Letter Number: {applicationStatus.offerLetter.letterNumber}
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={previewOfferLetter}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center"
              >
                <FileText className="h-4 w-4 mr-2" />
                Preview
              </button>
              <button
                onClick={downloadOfferLetter}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-green-800 font-medium">
                Congratulations! Your offer letter is ready for download.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {studentData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/student/profile"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <User className="h-5 w-5 text-primary-600 mr-3" />
              <div>
                <h4 className="font-medium text-gray-900">Update Profile</h4>
                <p className="text-sm text-gray-600">Edit personal details</p>
              </div>
            </Link>
            
            {applicationStatus?.status === 'accepted' && (
              <Link
                to="/student/payment"
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <CreditCard className="h-5 w-5 text-primary-600 mr-3" />
                <div>
                  <h4 className="font-medium text-gray-900">Make Payment</h4>
                  <p className="text-sm text-gray-600">Complete fee payment</p>
                </div>
              </Link>
            )}

            {/* <Link
              to="/contact"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <Mail className="h-5 w-5 text-primary-600 mr-3" />
              <div>
                <h4 className="font-medium text-gray-900">Contact Support</h4>
                <p className="text-sm text-gray-600">Get help & assistance</p>
              </div>
            </Link> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentStatus;